import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, CircleAlert, Clock3, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { changePassword } from "../../features/auth/api";
import {
  getLatestIdentityVerification,
  simulateIdentityVerificationSuccess,
  startIdentityVerification,
} from "../../entities/identity-verification/api";
import { getApiErrorMessage } from "../../shared/api/errors";
import { showToast } from "../../shared/ui/toast";
import SettingsNav from "./components/SettingsNav";

const INITIAL_FORM = {
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const verificationStatusMeta = {
  CREATED: { label: "已创建认证", tone: "pending", icon: Clock3 },
  PROCESSING: { label: "认证处理中", tone: "pending", icon: Clock3 },
  VERIFIED: { label: "已完成认证", tone: "success", icon: BadgeCheck },
  FAILED: { label: "认证未通过", tone: "error", icon: CircleAlert },
  EXPIRED: { label: "认证已过期", tone: "warning", icon: CircleAlert },
  CANCELLED: { label: "认证已取消", tone: "muted", icon: CircleAlert },
};
const pendingVerificationStatuses = new Set(["CREATED", "PROCESSING"]);
const identitySimulationEnabled = import.meta.env.DEV;

function formatVerificationTime(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function PasswordInput({
  id,
  label,
  value,
  autoComplete,
  visible,
  error,
  onChange,
  onToggle,
}) {
  const errorId = `${id}-error`;
  return (
    <div className="settings-field">
      <label htmlFor={id}>
        <span>{label}</span>
        <span className="settings-password-input">
          <input
            id={id}
            type={visible ? "text" : "password"}
            value={value}
            autoComplete={autoComplete}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            onChange={onChange}
            required
          />
          <button
            type="button"
            aria-label={visible ? `隐藏${label}` : `显示${label}`}
            aria-pressed={visible}
            onClick={onToggle}
          >
            {visible ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </span>
        {error ? <small id={errorId} className="settings-inline-error">{error}</small> : null}
      </label>
    </div>
  );
}

export default function AccountSecurityPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(INITIAL_FORM);
  const [visibleFields, setVisibleFields] = useState({});
  const [validationError, setValidationError] = useState("");
  const verificationQuery = useQuery({
    queryKey: ["identity-verification", "latest"],
    queryFn: getLatestIdentityVerification,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return pendingVerificationStatuses.has(status) ? 5_000 : false;
    },
  });
  const verification = verificationQuery.data;
  const verificationStatus = String(verification?.status || "").toUpperCase();
  const statusMeta = verificationStatusMeta[verificationStatus] || {
    label: verification ? "认证状态更新中" : "尚未实名认证",
    tone: "muted",
    icon: ShieldCheck,
  };
  const StatusIcon = statusMeta.icon;
  const verificationPending = pendingVerificationStatuses.has(verificationStatus);
  const verificationComplete = verificationStatus === "VERIFIED";
  const manualVerification = verification?.channel === "MANUAL";
  const verificationMutation = useMutation({
    mutationFn: () => startIdentityVerification({
      channel: identitySimulationEnabled ? "MANUAL" : "PROVIDER",
    }),
    onSuccess: async (created) => {
      queryClient.setQueryData(["identity-verification", "latest"], created);
      await queryClient.invalidateQueries({ queryKey: ["identity-verification", "latest"] });
      showToast(identitySimulationEnabled ? "模拟认证流程已创建" : "实名认证已发起");
    },
    onError: (error) => {
      showToast(getApiErrorMessage(error, "实名认证发起失败"), "error");
    },
  });
  const simulationMutation = useMutation({
    mutationFn: simulateIdentityVerificationSuccess,
    onSuccess: (verified) => {
      queryClient.setQueryData(["identity-verification", "latest"], verified);
      showToast("模拟实名认证已通过");
    },
    onError: (error) => {
      showToast(getApiErrorMessage(error, "模拟认证失败"), "error");
    },
  });
  const mutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      setForm(INITIAL_FORM);
      setValidationError("");
      setVisibleFields({});
      showToast("密码修改成功");
    },
    onError: (error) => showToast(getApiErrorMessage(error, "密码修改失败"), "error"),
  });

  function updateField(field, value) {
    setValidationError("");
    setForm((current) => ({ ...current, [field]: value }));
  }

  function toggleVisibility(field) {
    setVisibleFields((current) => ({ ...current, [field]: !current[field] }));
  }

  function submit(event) {
    event.preventDefault();
    if (form.newPassword.length < 8) {
      setValidationError("新密码至少需要 8 个字符");
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setValidationError("两次输入的新密码不一致");
      return;
    }
    if (form.oldPassword === form.newPassword) {
      setValidationError("新密码不能与原密码相同");
      return;
    }
    mutation.mutate({
      old_password: form.oldPassword,
      new_password: form.newPassword,
    });
  }

  return (
    <section className="settings-page">
      <SettingsNav />
      <div className="settings-content">
        <div className="settings-panel">
          <h2>账号与安全</h2>
          <p className="settings-description">管理实名认证与登录密码，保护你的账号和交易安全。</p>

          <section className="settings-security-section" aria-labelledby="identity-verification-title">
            <div className="settings-section-heading">
              <div>
                <h3 id="identity-verification-title">实名认证</h3>
                <p>用于确认账号主体身份，并保障接单、交易及资金安全。</p>
              </div>
              <span className={`identity-status-badge ${statusMeta.tone}`}>
                <StatusIcon size={16} />
                {statusMeta.label}
              </span>
            </div>

            {verificationQuery.isPending ? (
              <div className="identity-verification-state loading">正在查询认证状态……</div>
            ) : verificationQuery.isError ? (
              <div className="identity-verification-state error">
                <CircleAlert size={19} />
                <div>
                  <strong>认证状态加载失败</strong>
                  <span>{getApiErrorMessage(verificationQuery.error, "请稍后重新加载")}</span>
                </div>
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => verificationQuery.refetch()}
                >
                  重新加载
                </button>
              </div>
            ) : (
              <div className={`identity-verification-state ${statusMeta.tone}`}>
                <StatusIcon size={22} />
                <div>
                  <strong>{statusMeta.label}</strong>
                  <span>
                    {verificationComplete
                      ? `认证已于 ${formatVerificationTime(verification.verified_at)} 完成`
                      : verificationPending
                        ? "认证结果处理中，页面会自动更新状态"
                        : verification
                          ? "可以重新发起实名认证"
                          : "完成认证后可使用需要实名身份的服务"}
                  </span>
                  {verification?.verification_no ? (
                    <small>认证编号：{verification.verification_no}</small>
                  ) : null}
                  {verification?.expires_at ? (
                    <small>有效期至：{formatVerificationTime(verification.expires_at)}</small>
                  ) : null}
                </div>
              </div>
            )}

            <div className="identity-verification-actions">
              <button
                className="primary-button"
                type="button"
                disabled={
                  verificationQuery.isPending
                  || verificationQuery.isError
                  || verificationPending
                  || verificationComplete
                  || verificationMutation.isPending
                }
                onClick={() => verificationMutation.mutate()}
              >
                {verificationMutation.isPending
                  ? "正在发起……"
                  : verificationPending
                    ? "认证处理中"
                    : verificationComplete
                      ? "已完成认证"
                      : verification
                        ? identitySimulationEnabled ? "重新模拟认证" : "重新认证"
                        : identitySimulationEnabled ? "开始模拟认证" : "开始实名认证"}
              </button>
              {identitySimulationEnabled
              && manualVerification
              && ["PROCESSING", "VERIFIED"].includes(verificationStatus) ? (
                <button
                  className="secondary-button identity-simulation-button"
                  type="button"
                  disabled={simulationMutation.isPending}
                  onClick={() => simulationMutation.mutate()}
                >
                  {simulationMutation.isPending
                    ? "正在模拟……"
                    : verificationComplete
                      ? "验证模拟接口"
                      : "模拟认证通过"}
                </button>
                ) : null}
              <p>
                {identitySimulationEnabled
                  ? "当前为开发联调模式，模拟结果由后端生成，不使用真实身份信息。"
                  : "认证由平台实名服务完成。本页面不会展示或保存身份证号码等敏感信息。"}
              </p>
            </div>
          </section>

          <section className="settings-security-section" aria-labelledby="password-security-title">
            <div className="settings-section-heading">
              <div>
                <h3 id="password-security-title">登录密码</h3>
                <p>定期更新密码可以降低账号被盗风险。</p>
              </div>
            </div>
            <form className="settings-form settings-security-form" onSubmit={submit}>
            <PasswordInput
              id="old-password"
              label="当前密码"
              value={form.oldPassword}
              autoComplete="current-password"
              visible={visibleFields.oldPassword}
              onChange={(event) => updateField("oldPassword", event.target.value)}
              onToggle={() => toggleVisibility("oldPassword")}
            />
            <PasswordInput
              id="new-password"
              label="新密码"
              value={form.newPassword}
              autoComplete="new-password"
              visible={visibleFields.newPassword}
              error={
                form.newPassword && form.newPassword.length < 8
                  ? "新密码至少需要 8 个字符"
                  : ""
              }
              onChange={(event) => updateField("newPassword", event.target.value)}
              onToggle={() => toggleVisibility("newPassword")}
            />
            <PasswordInput
              id="confirm-password"
              label="确认新密码"
              value={form.confirmPassword}
              autoComplete="new-password"
              visible={visibleFields.confirmPassword}
              error={
                form.confirmPassword && form.newPassword !== form.confirmPassword
                  ? "两次输入的新密码不一致"
                  : ""
              }
              onChange={(event) => updateField("confirmPassword", event.target.value)}
              onToggle={() => toggleVisibility("confirmPassword")}
            />
            {validationError ? <p className="form-error">{validationError}</p> : null}
            {mutation.isError ? (
              <p className="form-error">{getApiErrorMessage(mutation.error, "密码修改失败")}</p>
            ) : null}
            <button
              className="primary-button"
              type="submit"
              disabled={
                mutation.isPending
                || !form.oldPassword
                || form.newPassword.length < 8
                || form.newPassword !== form.confirmPassword
              }
            >
              {mutation.isPending ? "修改中……" : "修改密码"}
            </button>
            </form>
          </section>
        </div>
      </div>
    </section>
  );
}
